import {
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
  useLayoutEffect,
  useRef,
} from 'react';

import styles from './ConnectCards.module.css';

import { Card, type CardProps } from '../Card';

interface ConnectCardProps
  extends PropsWithChildren<HTMLAttributes<HTMLElement>>, CardProps {}

/*
 * Get the element with the data-connect-to-card attribute and verify it exists.
 * Only returns elements that are not hidden (via hidden attribute on the element or its parents).
 * Uses offsetParent for efficient visibility checking - it's null for hidden elements.
 */
const getConnectElement = (cardElement: HTMLElement) => {
  const allConnectElements = cardElement.querySelectorAll<HTMLElement>(
    '[data-connect-to-card]'
  );

  // Find the first element that is not hidden (offsetParent is null for hidden elements)
  for (const element of allConnectElements) {
    if (element.offsetParent !== null) {
      return element;
    }
  }

  if (allConnectElements.length === 0) {
    console.error(
      'No child component found with the `data-connect-to-card` attribute'
    );
  }

  return null;
};

/*
 * Get the relative position of the dot to the container that uses it as a pseudo-element.
 */
const getConnectDotPosition = (
  connectElement: HTMLElement
): { top: number; bottom: number } => {
  const connectDotStyle = getComputedStyle(connectElement, ':before');
  return {
    // use parseInt to get just the numeric values in front of the 'px' unit
    top: parseInt(connectDotStyle.top, 10),
    bottom: parseInt(connectDotStyle.bottom, 10),
  };
};

/*
 * Get the CSS variables defined in the style module to use for calculations.
 */
const getConnectCssVariables = (cardElement: HTMLElement) => {
  const cardComputedStyle = getComputedStyle(cardElement);
  return {
    // add in the border spacing twice (once for this card and once for the card above this one)
    connectCardsGap:
      parseInt(
        cardComputedStyle.getPropertyValue('--tfd-connect-cards-gap'),
        10
      ) + 2,
    // we want to have a consistent distance from the dot itself instead of relying on the dot's container
    connectDotGap: parseInt(
      cardComputedStyle.getPropertyValue('--tfd-connect-cards-dot-gap'),
      10
    ),
  };
};

const updateTopLinePosition = (
  cardElement: HTMLElement,
  topLineElement: HTMLElement
) => {
  const connectElement = getConnectElement(cardElement);

  if (!connectElement) {
    return;
  }

  const { connectCardsGap, connectDotGap } =
    getConnectCssVariables(cardElement);

  const { top: connectDotTop } = getConnectDotPosition(connectElement);

  // get the relative offset from the top of the card
  const topOfConnectDot =
    connectElement.getBoundingClientRect().top +
    connectDotTop -
    cardElement.getBoundingClientRect().top -
    1; // account for the border width

  // the top line should go a little higher to fill the gap between cards
  topLineElement.style.top = `-${connectCardsGap}px`;
  topLineElement.style.height = `${topOfConnectDot + connectCardsGap - connectDotGap}px`;
};

const updateBottomLinePosition = (
  cardElement: HTMLElement,
  bottomLineElement: HTMLElement
) => {
  const connectElement = getConnectElement(cardElement);

  if (!connectElement) {
    return;
  }

  const { connectDotGap } = getConnectCssVariables(cardElement);

  const { bottom: connectDotBottom } = getConnectDotPosition(connectElement);

  // get the relative offset from the bottom of the card
  const bottomOfConnectDot =
    cardElement.getBoundingClientRect().bottom -
    (connectElement.getBoundingClientRect().bottom - connectDotBottom) -
    1; // account for the border width

  bottomLineElement.style.bottom = '0px';
  bottomLineElement.style.height = `${bottomOfConnectDot - connectDotGap}px`;
};

export function ConnectCard({
  children,
  ...props
}: ConnectCardProps): ReactNode {
  const cardRef = useRef<HTMLDivElement>(null);
  const topLineRef = useRef<HTMLDivElement>(null);
  const bottomLineRef = useRef<HTMLDivElement>(null);

  const updateLinePositions = () => {
    if (cardRef.current) {
      if (topLineRef.current) {
        updateTopLinePosition(cardRef.current, topLineRef.current);
      }
      if (bottomLineRef.current) {
        updateBottomLinePosition(cardRef.current, bottomLineRef.current);
      }
    }
  };

  useLayoutEffect(() => {
    if (!cardRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateLinePositions();
    });

    // initial paint, set the line positions right away
    updateLinePositions();
    resizeObserver.observe(cardRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Card {...props} ref={cardRef} className={styles.connectCard}>
      <div ref={topLineRef} className={styles.connectLine} />
      {children}
      <div ref={bottomLineRef} className={styles.connectLine} />
    </Card>
  );
}
